import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useState } from 'react';
import { useRequest } from 'ahooks';
import {
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    InputNumber,
    TreeSelect,
    Icon,
    Radio,
    Result,
    Button,
    Switch,
} from 'udesk-ui';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import tranToTaskCenterNotification from 'Component/common/task-notification';
import Frequency, { checkRequird } from 'src/components/common/Frequency';
import TimeRange from './TimeRange';
import {
    getSmartTagsModelGenerate,
    deleteSmartTagsModelGenerate,
} from 'src/api/smartTags/model/generate';
import './tag-extract-modal.less';

const TaskType = {
    Immediate: 1,
    Timed: 2,
};

const { RangePicker } = DatePicker;

// 数据源选项
const DATA_SOURCE_OPTIONS = [
    {
        label: /* 语音通话 */ UdeskLocales['current'].pages.intelligentAnalysis.intelligentTags
            .components.tagExtractModal.voiceCall,
        value: 1,
    },
    {
        label: /* 文本对话 */ UdeskLocales['current'].pages.intelligentAnalysis.intelligentTags
            .components.tagExtractModal.textDialogue,
        value: 2,
    },
    {
        label: /* 工单 */ UdeskLocales['current'].pages.intelligentAnalysis.intelligentTags
            .components.tagExtractModal.workOrder,
        value: 10,
    },
];

const TagExtractModal = ({ visible, loading, onCancel, onOk, smartTagCategorys = [] }) => {
    const [isTaskRunning, setIsTaskRunnig] = useState(false);
    const [prevData, setPrevData] = useState();

    const { loading: getLoading } = useRequest(
        () => getSmartTagsModelGenerate({ params: { systemModule: 1 } }),
        {
            ready: visible,
            onSuccess: (res) => {
                setIsTaskRunnig(!!res.data);
                setPrevData({
                    ...res.data,
                    dateRange:
                        res.data.startTime && res.data.endTime
                            ? [moment(res.data.startTime), moment(res.data.endTime)]
                            : getDefaultTimeRange(),
                    frequencyData: {
                        frequency: res.data.frequency,
                        day: res.data.day,
                        week: res.data.week,
                        time: res.data.time,
                    },
                    smartTagCategoryId: `${res.data.smartTagCategoryId}`,
                    isDefaultValid: res.data.isDefaultValid === 1,
                });
            },
        }
    );

    const handleFinishTask = () => {
        deleteSmartTagsModelGenerate({
            params: {
                systemModule: 1,
            },
        }).then(() => {
            setIsTaskRunnig(false);
            form.setFieldsValue({
                ...prevData,
                taskType: TaskType.Timed,
            });
        });
    };

    const history = useHistory();

    const [form] = Form.useForm();

    const getDefaultTimeRange = () => {
        return [moment().subtract(24, 'hours'), moment()];
    };

    const [initialValues] = useState({
        dateRange: getDefaultTimeRange(),
        limitNum: 50,
        taskType: TaskType.Immediate,
        frequencyData: {
            frequency: Udesk.enums.assessFrequencyTypes.everyDay.id,
        },
    });

    const [showSuccess, setShowSuccess] = useState(false);

    const handleOk = () => {
        form.validateFields()
            .then((values) => {
                const { taskType, dateRange } = values;

                const formattedValues = {
                    taskType,
                    inspectDataSources: values.inspectDataSources,
                    smartTagCategoryId: values.smartTagCategoryId,
                    limitNum: values.limitNum,
                    require: values.require,
                    systemModule: 1,

                    startTime: dateRange?.[0]?.format('YYYY-MM-DD HH:mm:ss'),
                    endTime: dateRange?.[1]?.format('YYYY-MM-DD HH:mm:ss'),

                    frequency: values.frequencyData?.frequency,
                    day: values.frequencyData?.day,
                    week: values.frequencyData?.week,
                    time: values.frequencyData?.time,
                    scope: values.scope,

                    isDefaultValid: values.isDefaultValid ? 1 : 0,
                };

                onOk(formattedValues).then(() => {
                    tranToTaskCenterNotification();
                    onCancel(); // 关闭标签抽取弹窗
                    setShowSuccess(true); // 显示成功提示
                });
            })
            .catch((err) => {
                console.error(
                    /* 表单验证失败: */ UdeskLocales['current'].pages.intelligentAnalysis
                        .intelligentTags.components.tagExtractModal.formValidationFailed,
                    err
                );
            });
    };

    const handleSuccessClose = () => {
        form.resetFields();
        setShowSuccess(false);
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel(); // 关闭标签抽取弹窗
    };

    const handleCheckTask = () => {
        form.resetFields();
        setShowSuccess(false);
        history.push('/site/admin/task-center/task-list');
    };

    return (
        <>
            {visible && !getLoading && (
                <Modal
                    title={
                        /* 标签抽取 */ UdeskLocales['current'].pages.intelligentAnalysis
                            .intelligentTags.components.tagExtractModal.labelExtraction
                    }
                    visible={visible}
                    onCancel={handleCancel}
                    onOk={handleOk}
                    confirmLoading={loading}
                    destroyOnClose
                    width={800}
                    footer={isTaskRunning ? null : undefined}
                >
                    {!isTaskRunning ? (
                        <Form
                            form={form}
                            layout="horizontal"
                            initialValues={initialValues}
                            labelCol={{ span: 4 }}
                            wrapperCol={{ span: 20 }}
                        >
                            <Form.Item
                                name="taskType"
                                label={
                                    /* 任务类型 */ UdeskLocales['current'].pages.intelligentAnalysis
                                        .intelligentTags.components.tagExtractModal.taskType
                                }
                                rules={[
                                    {
                                        required: true,
                                    },
                                ]}
                            >
                                <Radio.Group
                                    options={[
                                        {
                                            label: /* 立即执行 */ UdeskLocales['current'].pages
                                                .intelligentAnalysis.intelligentTags.components
                                                .tagExtractModal.immediateExecution,
                                            value: TaskType.Immediate,
                                        },
                                        {
                                            label: /* 定时执行 */ UdeskLocales['current'].pages
                                                .intelligentAnalysis.intelligentTags.components
                                                .tagExtractModal.timingExecution,
                                            value: TaskType.Timed,
                                        },
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item
                                label={
                                    UdeskLocales['current'].pages.intelligentAnalysis
                                        .intelligentTags.defaultValidity
                                }
                                name="isDefaultValid"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>

                            <Form.Item
                                name="inspectDataSources"
                                label={
                                    /* 数据源 */ UdeskLocales['current'].pages.intelligentAnalysis
                                        .intelligentTags.components.tagExtractModal.dataSource
                                }
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            /* 请选择数据源 */ UdeskLocales['current'].pages
                                                .intelligentAnalysis.intelligentTags.components
                                                .tagExtractModal.pleaseSelectADataSource,
                                    },
                                ]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder={
                                        /* 请选择数据源 */ UdeskLocales['current'].pages
                                            .intelligentAnalysis.intelligentTags.components
                                            .tagExtractModal.pleaseSelectADataSource
                                    }
                                    options={DATA_SOURCE_OPTIONS}
                                />
                            </Form.Item>

                            <Form.Item noStyle shouldUpdate>
                                {({ getFieldsValue }) => {
                                    const taskType = getFieldsValue().taskType;

                                    return taskType === TaskType.Timed ? (
                                        <Form.Item
                                            name="frequencyData"
                                            label={
                                                /* 执行频率 */ UdeskLocales['current'].pages
                                                    .intelligentAnalysis.intelligentTags.components
                                                    .tagExtractModal.executionFrequency
                                            }
                                            rules={[
                                                {
                                                    required: true,
                                                    validator: (_, value) => {
                                                        if (checkRequird(value)) {
                                                            return Promise.resolve();
                                                        } else {
                                                            return Promise.reject(
                                                                /* 请选择执行频率 */ UdeskLocales[
                                                                    'current'
                                                                ].pages.intelligentAnalysis
                                                                    .intelligentTags.components
                                                                    .tagExtractModal
                                                                    .pleaseSelectAnExecutionFrequency
                                                            );
                                                        }
                                                    },
                                                },
                                            ]}
                                        >
                                            <Frequency
                                                onFrequencyChange={() => {
                                                    form.setFieldsValue({
                                                        scope: undefined,
                                                    });
                                                }}
                                            />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>

                            <Form.Item noStyle shouldUpdate>
                                {({ getFieldsValue }) => {
                                    const taskType = getFieldsValue().taskType;
                                    const range = getFieldsValue().frequencyData?.frequency;

                                    return taskType === TaskType.Immediate ? (
                                        <Form.Item
                                            name="dateRange"
                                            label={
                                                /* 数据时间范围 */ UdeskLocales['current'].pages
                                                    .intelligentAnalysis.intelligentTags.components
                                                    .tagExtractModal.dataTimeRange
                                            }
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        /* 请选择时间范围 */ UdeskLocales['current']
                                                            .pages.intelligentAnalysis
                                                            .intelligentTags.components
                                                            .tagExtractModal.pleaseSelectATimeRange,
                                                },
                                                {
                                                    validator: (_, value) => {
                                                        if (value && value[0] && value[1]) {
                                                            const diffDays = value[1].diff(
                                                                value[0],
                                                                'seconds'
                                                            );
                                                            if (diffDays > 30 * 24 * 60 * 60) {
                                                                return Promise.reject(
                                                                    /* 时间范围不能超过30天 */ UdeskLocales[
                                                                        'current'
                                                                    ].pages.intelligentAnalysis
                                                                        .intelligentTags.components
                                                                        .tagExtractModal
                                                                        .timeFrameCannotExceedDays
                                                                );
                                                            }
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                },
                                            ]}
                                            extra={
                                                /* 最多选择三十天数据 */ UdeskLocales['current']
                                                    .pages.intelligentAnalysis.intelligentTags
                                                    .components.tagExtractModal.selectUpToDaysOfData
                                            }
                                        >
                                            <RangePicker
                                                showTime
                                                format="YYYY-MM-DD HH:mm:ss"
                                                disabledDate={(current) => {
                                                    if (!current) return false;

                                                    // 禁用未来日期
                                                    if (current > moment()) return true;

                                                    return false;
                                                }}
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>
                                    ) : (
                                        <Form.Item
                                            name="scope"
                                            label={
                                                /* 数据时间范围 */ UdeskLocales['current'].pages
                                                    .intelligentAnalysis.intelligentTags.components
                                                    .tagExtractModal.dataTimeRange
                                            }
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        /* 请选择时间范围 */ UdeskLocales['current']
                                                            .pages.intelligentAnalysis
                                                            .intelligentTags.components
                                                            .tagExtractModal.pleaseSelectATimeRange,
                                                },
                                            ]}
                                        >
                                            <TimeRange
                                                range={range}
                                                placeholder={
                                                    /* 请选择时间范围 */ UdeskLocales['current']
                                                        .pages.intelligentAnalysis.intelligentTags
                                                        .components.tagExtractModal
                                                        .pleaseSelectATimeRange
                                                }
                                            />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>

                            <Form.Item
                                name="limitNum"
                                label={
                                    /* 抽取数量上限 */ UdeskLocales['current'].pages
                                        .intelligentAnalysis.intelligentTags.components
                                        .tagExtractModal.extractionQuantityUpperLimit
                                }
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            /* 请输入抽取数量上限 */ UdeskLocales['current'].pages
                                                .intelligentAnalysis.intelligentTags.components
                                                .tagExtractModal
                                                .pleaseEnterTheMaximumExtractionQuantity,
                                    },
                                ]}
                            >
                                <InputNumber
                                    min={1}
                                    max={300}
                                    placeholder={
                                        /* 请输入抽取数量上限 */ UdeskLocales['current'].pages
                                            .intelligentAnalysis.intelligentTags.components
                                            .tagExtractModal.pleaseEnterTheMaximumExtractionQuantity
                                    }
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>

                            <Form.Item
                                name="smartTagCategoryId"
                                label={
                                    /* 添加至分类 */ UdeskLocales['current'].pages
                                        .intelligentAnalysis.intelligentTags.components
                                        .tagExtractModal.addToClassification
                                }
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            /* 请选择标签分类 */ UdeskLocales['current'].pages
                                                .intelligentAnalysis.intelligentTags.components
                                                .tagExtractModal.pleaseSelectALabelClassification,
                                    },
                                ]}
                            >
                                <TreeSelect
                                    treeData={smartTagCategorys}
                                    placeholder={
                                        /* 请选择标签分类 */ UdeskLocales['current'].pages
                                            .intelligentAnalysis.intelligentTags.components
                                            .tagExtractModal.pleaseSelectALabelClassification
                                    }
                                    treeDefaultExpandAll
                                    showSearch
                                    allowClear
                                    treeNodeFilterProp="title"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>

                            <Form.Item
                                name="require"
                                label={
                                    /* 抽取要求 */ UdeskLocales['current'].pages.intelligentAnalysis
                                        .intelligentTags.components.tagExtractModal
                                        .extractionRequirements
                                }
                            >
                                <Input.TextArea
                                    rows={4}
                                    placeholder={
                                        /* 请输入抽取要求（选填） */ UdeskLocales['current'].pages
                                            .intelligentAnalysis.intelligentTags.components
                                            .tagExtractModal
                                            .pleaseEnterExtractionRequirementsoptional
                                    }
                                />
                            </Form.Item>
                        </Form>
                    ) : (
                        <Result
                            status="success"
                            title={
                                /* 定时任务生效中 */ UdeskLocales['current'].pages
                                    .intelligentAnalysis.intelligentTags.components.tagExtractModal
                                    .scheduledTaskInEffect
                            }
                            subTitle={
                                /* 请在数据处理完成后开始使用 */ UdeskLocales['current'].pages
                                    .intelligentAnalysis.intelligentTags.components.tagExtractModal
                                    .pleaseStartUsingAfterDataProcessingIsComplete
                            }
                            extra={[
                                <Button type="primary" key="finish" onClick={handleFinishTask}>
                                    {/* 结束任务 */}
                                    {
                                        UdeskLocales['current'].pages.intelligentAnalysis
                                            .intelligentTags.components.tagExtractModal.endTask
                                    }
                                </Button>,
                                <Button key="check" onClick={handleCheckTask}>
                                    {/* 查看任务 */}
                                    {
                                        UdeskLocales['current'].pages.intelligentAnalysis
                                            .intelligentTags.components.tagExtractModal.viewTasks
                                    }
                                </Button>,
                            ]}
                        />
                    )}
                </Modal>
            )}

            {showSuccess && (
                <div className="success-dialog-wrapper">
                    <div className="success-dialog">
                        <span className="close-icon" onClick={handleSuccessClose}>
                            <Icon type="CloseOutlined" antdIcon={true} />
                        </span>
                        <div className="success-content">
                            <div className="success-icon">
                                <Icon type="CheckCircleOutlined" antdIcon={true} />
                            </div>
                            <h3 className="success-title">
                                {/* 提交成功，开始创建标签中 */}
                                {
                                    UdeskLocales['current'].pages.intelligentAnalysis
                                        .intelligentTags.components.tagExtractModal
                                        .submittedSuccessfullyStartCreatingTags
                                }
                            </h3>
                            <p className="success-desc">
                                {/* 请在数据处理完成后开始使用 */}
                                {
                                    UdeskLocales['current'].pages.intelligentAnalysis
                                        .intelligentTags.components.tagExtractModal
                                        .pleaseStartUsingAfterDataProcessingIsComplete
                                }
                            </p>
                            <div className="success-btns">
                                <button className="btn-later" onClick={handleSuccessClose}>
                                    {/* 稍后查看 */}
                                    {
                                        UdeskLocales['current'].pages.intelligentAnalysis
                                            .intelligentTags.components.tagExtractModal.checkLater
                                    }
                                </button>
                                <button className="btn-check-task" onClick={handleCheckTask}>
                                    {/* 查看任务 */}
                                    {
                                        UdeskLocales['current'].pages.intelligentAnalysis
                                            .intelligentTags.components.tagExtractModal.viewTasks
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TagExtractModal;
